import { FJDecodeMode, FJDecoderManager, FJEncodeMode, FJEncoderManager } from '@fj/codec';
import FJConstant from '../../constants/FJConstant';
import FJUtil from '../../util/FJUtil';
import FJPoolStore from '../FJPoolStore';
import FJVideoRemoveBaseManage from './FJVideoRemoveBaseManage';
import FJVideoUtil from '../../util/FJVideoUtil';

// 视频删除背景后处理
class FJVideoRemoveAfterManage extends FJVideoRemoveBaseManage {
    _exportCanvas = null;
    _exportCtx = null;

    constructor() {
        super();
    }

    /**
     * @desc 绘制画面
     */
    async draw() {
        if (this._loadStatus !== FJConstant.SUCCESS || !this._source || !this._ctx) return;

        super.draw();

        this._ctx.save();
        this._ctx.globalCompositeOperation = 'destination-over';
        this._ctx.drawImage(this._backgroundCanvas, 0, 0, this._sourceInfo.width, this._sourceInfo.height);
        this._ctx.restore();
    }

    /**
     * @desc 设置视频源
     * @param {Object} source
     */
    async setSource(src) {
        try {
            const video = document.createElement('video');
            video.crossOrigin = 'anonymous';
            this._loadStatus = FJConstant.LOADING;

            this._sourceUrl = src;
            let videoBlob = await FJUtil.getBlobByUrl(src);
            if (!videoBlob) {
                videoBlob = await FJUtil.getBlobByUrl(src + '?v=' + Date.now());
            }

            if (!videoBlob) {
                throw new Error('Failed to load video');
            }

            const blobUrl = URL.createObjectURL(videoBlob);

            const sourceInfo = await new Promise((resolve, reject) => {
                video.oncanplay = () => {
                    const fileData = {
                        width: video.videoWidth,
                        height: video.videoHeight,
                    };
                    this._duration = video.duration;

                    this._loadStatus = FJConstant.SUCCESS;
                    resolve(fileData);
                };
                video.onerror = () => {
                    this._loadStatus = FJConstant.ERROR;
                    reject();
                };

                video.src = blobUrl;
            });

            this._source = video;
            this._sourceInfo = sourceInfo;

            const file = new File([videoBlob], 'video.webm', { type: 'video/webm' });
            this._sourceFile = file;

            this.updateBackgroundCanvas();
        } catch (error) {
            this._loadStatus = FJConstant.ERROR;
            throw error;
        }
    }

    createExportCanvas() {
        if (!this._exportCanvas) {
            this._exportCanvas = FJPoolStore.getCanvas('2d');
            this._exportCtx = this._exportCanvas.getContext('2d', { willReadFrequently: true });
        }

        return { exportCanvas: this._exportCanvas, exportCtx: this._exportCtx };
    }

    removeExportCanvas() {
        if (!this._exportCanvas) return;
        FJPoolStore.releaseCanvas(this._exportCanvas);
        this._exportCanvas = null;
        this._exportCtx = null;
    }

    /**
     * @param {Function} options.onProgress 进度回调，回调参数 {Number} progress - 进度，0 - 1
     * @param {Function} options.onComplete 完成回调，回调参数 {Blob} blob - 导出的视频文件
     * @param {Function} options.onError 错误回调，回调参数 {Error} error - 错误信息
     * @param {Boolean} options.isGif 是否导出gif
     * @param {Boolean} options.isTransparent 是否透明背景
     */
    async exportVideo(options) {
        const { onProgress, onComplete, onError, isGif = false, isTransparent = false } = options;

        const start = 0;
        const end = this._source.duration;
        const file = this._sourceFile;

        const { exportCanvas, exportCtx } = this.createExportCanvas(isGif);

        (async () => {
            const audioConfig = {
                bitrate: 256 * 1000,

                samplerate: FJUtil.getAudioSampleRate(),
            };

            // TODO fj-codec 修复 importScripts 后可以删除
            /**
             * 目前断网情况下 importScripts codec.js 的错误没有正确处理
             * 所以这里使用 setTimeout 配合 navigator.onLine 来判断是否断网
             * 如果断网就抛出异常，因为断网情况下如果 codec.js 没有缓存的话获取视频信息会卡住
             */
            const { width, height, hasAudio, hasVideo, rotate } = await Promise.race([
                FJDecoderManager.getVideoInfo(file),
                new Promise((resolve, reject) => {
                    setTimeout(() => {
                        if (!navigator.onLine) {
                            reject(new Error('No internet connection'));
                        }
                    }, 3000);
                }),
            ]);

            if (!hasVideo) {
                onError(new Error('No video found'));
                return;
            }

            // 导出gif时，帧率固定为10, 视频时使用源视频帧率 gif 输出尺寸设置为 最长边480px
            // 导出视频时，帧率固定在30帧
            const FPS = isGif ? 10 : 30;
            const { width: fitWidth, height: fitHeight } = isGif
                ? FJUtil.fitSize({ width, height }, { width: 480, height: 480 }, false)
                : { width, height };

            exportCanvas.width = fitWidth;
            exportCanvas.height = fitHeight;

            const videoConfig = {
                fps: FPS,
                width: rotate === 90 || rotate === 270 ? fitHeight : fitWidth,
                height: rotate === 90 || rotate === 270 ? fitWidth : fitHeight,
                presetIdx: 0, // h264编码参数，控制输出速度和质量。0代表最快，0-7依次对应"ultrafast", "superfast", "veryfast", "faster", "fast", "medium", "slow", "veryslow"
                codecIdx: isGif ? 2 : 1, // 编码器索引，0代表vpx，1代表h264, 2代表gif
                formatIdx: isGif ? 2 : 1, // 格式索引，0代表webm，1代表mp4, 2代表gif

                // 以下 3 个参数是 gif 输出透明背景时使用
                keepAlpha: isGif && isTransparent,
                gifAlphaNumWorkers: 6, // 工作线程
                gifAlphaSampleInterval: 40, // 采样间隔，越小越精确，但是性能会下降

                loop: 0, // GIF编码参数，0代表无限循环播放, 1代表播放一次
                mode: !isGif && FJUtil.couldUseWebCodecs('encode') ? FJEncodeMode.WEBCODECS : FJEncodeMode.FFMPEG,
                quality: {
                    crf: 20,
                    bitrate: 10000000,
                },

                bitrateMode: 'variable', // h264编码参数，控制输出速度和质量。constant代表恒定码率，variable代表可变码率
                exportQuality: 1080,
                concurrentSize: 2,
            };

            await new Promise(resolve => {
                const encoderOptions = {
                    initCallback: resolve,
                    doneCallback: data => {
                        const blob = new Blob([data], { type: isGif ? 'image/gif' : 'video/mp4' });
                        onComplete(blob, isGif ? 'gif' : 'mp4');
                        FJEncoderManager.cancel();
                    },
                    chunkCallback: () => {},

                    errorCallback: onError,
                    encodeCallback: () => {},
                };
                FJEncoderManager.initEncoder(videoConfig, audioConfig, encoderOptions);
            });

            if (hasAudio && !isGif) {
                FJEncoderManager.markAudioEncode(true);
            }

            const config = {
                fileName: file.name,
                width: fitWidth,
                height: fitHeight,
                start,
                end,
                speed: 1,
                fps: FPS,
                samplerate: FJUtil.getAudioSampleRate(),
                mode: FJDecodeMode.FFMPEG,
                frameType: FJVideoUtil.getDecodeVideoFrameType(),
                ignoreNoValidDuration: true,
            };

            let count = 0;
            let timestampPerFrame = (1000 * 1000) / FPS;
            let maxCount = 0;
            if (end === Infinity) {
                const { duration: decodeDuration } = await FJDecoderManager.getVideoInfo(file, {
                    ignoreNoValidDuration: true,
                });
                let duration = decodeDuration;
                if (duration <= 0) {
                    duration = (await FJDecoderManager.getVideoInfoByElement(file))?.duration || Infinity;
                }
                maxCount = (duration - start) * FPS;
            } else {
                maxCount = (end - start) * FPS;
            }

            // 解码视频
            await new Promise((resolve, reject) => {
                FJDecoderManager.decodeVideo(file, config, {
                    decodeCallback: frame => {
                        try {
                            const { data, error, ended } = frame;
                            if (error) {
                                reject(error);
                                return;
                            }

                            if (ended) {
                                FJEncoderManager.markVideoFrameDecodeDone();
                                resolve();
                                return;
                            }

                            const { frameWidth, frameHeight, data: imageSourceFromData } = data;

                            exportCanvas.width = 1;
                            exportCanvas.height = 1;

                            exportCanvas.width = frameWidth;
                            exportCanvas.height = frameHeight;

                            exportCtx.clearRect(0, 0, frameWidth, frameHeight);
                            exportCtx.drawImage(imageSourceFromData, 0, 0, frameWidth, frameHeight);

                            if (!isTransparent) {
                                exportCtx.save();
                                exportCtx.globalCompositeOperation = 'destination-over';
                                exportCtx.drawImage(this._backgroundCanvas, 0, 0, frameWidth, frameHeight);
                                exportCtx.restore();
                            }

                            imageSourceFromData?.getContext &&
                                imageSourceFromData?.getContext('2d')?.clearRect(0, 0, frameWidth, frameHeight);

                            let imageSource = exportCanvas;

                            const couldOptFrame = FJUtil.couldOptFrame();
                            let frameType = isGif ? 'IMAGEDATA' : 'VIDEOFRAME';
                            let vf = null;

                            if (
                                typeof VideoFrame === 'undefined' ||
                                FJUtil.getBrowser() === 'FF' ||
                                FJUtil.getBrowser() === 'Safari'
                            ) {
                                vf = couldOptFrame
                                    ? FJUtil.getImageBitmap(exportCanvas)
                                    : exportCtx.getImageData(0, 0, exportCanvas.width, exportCanvas.height);
                                frameType = couldOptFrame ? 'IMAGEBITMAP' : 'IMAGEDATA';
                                count++;
                            } else {
                                const sourceWidth = frameWidth;
                                const sourceHeight = frameHeight;

                                if (frameType === 'VIDEOFRAME') {
                                    // eslint-disable-next-line no-undef
                                    vf = new VideoFrame(exportCanvas, {
                                        // format: 'RGBX',
                                        alpha: 'discard',
                                        displayWidth: sourceWidth,
                                        displayHeight: sourceHeight,
                                        duration: timestampPerFrame,
                                        timestamp: timestampPerFrame * ++count,
                                    });
                                }

                                if (frameType === 'IMAGEDATA') {
                                    count++;
                                    vf = exportCtx.getImageData(0, 0, frameWidth, frameHeight);
                                }
                            }

                            FJEncoderManager.addVideoFrame({ type: frameType, data: vf });
                            imageSource.close && imageSource.close();
                            onProgress(count / maxCount);
                        } catch (error) {
                            reject(error);
                        }
                    },
                    getValidFramesSize: () => {
                        return FJEncoderManager.getValidFramesSize();
                    },
                });
            });

            // 编码音频
            if (!isGif && hasAudio) {
                const audioData = await FJDecoderManager.decodeAudioOverall(file, {
                    start,
                    end,
                    samplerate: FJUtil.getAudioSampleRate(),
                    mode: FJDecodeMode.FFMPEG,
                    speed: 1,
                    fileType: 'video',
                    ignoreNoValidDuration: true,
                });
                FJEncoderManager.markAudioEncode(false);
                FJEncoderManager.addAudioChunk(audioData.buffer, true);
            }
        })()
            .catch(e => {
                onError(e);
            })
            .finally(() => {
                this.removeExportCanvas();
            });
    }

    /**
     * @desc 取消导出视频
     */
    cancelExport() {
        FJDecoderManager.cancelDecodersForExport();
        FJEncoderManager.cancel();
        this.removeExportCanvas();
    }

    async reloadSource() {
        await this.setSource(this._sourceUrl);
    }
}

const FJVideoRemoveAfterManageSingleton = new FJVideoRemoveAfterManage();
export default FJVideoRemoveAfterManageSingleton;

export { FJVideoRemoveAfterManage };
